<script lang="ts">
  import type { Transaction } from '../../types/transaction';

  let {
    transactions = [],
    onMonthSelect = (_year: number, _month: number) => {},
    currentYear = null,
    currentMonth = null
  }: {
    transactions: Transaction[];
    onMonthSelect: (year: number, month: number) => void;
    currentYear?: number | null;
    currentMonth?: number | null;
  } = $props();

  let hoveredYear = $state<number | null>(null);
  let hoveredMonth = $state<number | null>(null);
  let tooltipData = $state<{ text: string; top: number; left: number } | null>(null);

  function handleMouseMove(event: MouseEvent) {
    const dots = document.querySelectorAll('.month-dot');
    let closestDot: HTMLElement | null = null;
    let closestDistance = Infinity;

    dots.forEach(dot => {
      const rect = dot.getBoundingClientRect();
      const dotCenterY = rect.top + rect.height / 2;
      const distance = Math.abs(event.clientY - dotCenterY);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestDot = dot as HTMLElement;
      }
    });

    if (closestDot) {
      const year = parseInt(closestDot.dataset.year || '0');
      const month = parseInt(closestDot.dataset.month || '0');
      const count = parseInt(closestDot.dataset.count || '0');
      const rect = closestDot.getBoundingClientRect();

      hoveredYear = year;
      hoveredMonth = month;
      tooltipData = {
        text: `${monthNames[month]} ${year} (${count})`,
        top: event.clientY,
        left: rect.left - 8
      };
    }
  }

  function handleMouseLeave() {
    hoveredYear = null;
    hoveredMonth = null;
    tooltipData = null;
  }

  function handleClick(event: MouseEvent) {
    if (hoveredYear !== null && hoveredMonth !== null) {
      onMonthSelect(hoveredYear, hoveredMonth);
    }
  }

  // Group transactions by year and month
  let monthGroups = $derived.by(() => {
    const groups = new Map<number, Map<number, number>>();

    transactions.forEach(t => {
      const date = new Date(t.date);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-11

      if (!groups.has(year)) {
        groups.set(year, new Map());
      }
      const yearMap = groups.get(year)!;
      yearMap.set(month, (yearMap.get(month) || 0) + 1);
    });

    // Convert to sorted array
    const result: { year: number; months: { month: number; count: number }[] }[] = [];
    const sortedYears = Array.from(groups.keys()).sort((a, b) => b - a);

    sortedYears.forEach(year => {
      const monthMap = groups.get(year)!;
      const months = Array.from(monthMap.entries())
        .map(([month, count]) => ({ month, count }))
        .sort((a, b) => b.month - a.month);

      result.push({ year, months });
    });

    return result;
  });

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
</script>

<div class="month-navigator" onmousemove={handleMouseMove} onmouseleave={handleMouseLeave} onclick={handleClick}>
  <div class="navigator-content">
    {#each monthGroups as { year, months }}
      <div class="year-section">
        <div class="year-label">{year}</div>

        <div class="dots-container">
          {#each months as { month, count }}
            <button
              class="month-dot"
              class:current={currentYear === year && currentMonth === month}
              class:hovered={hoveredYear === year && hoveredMonth === month}
              data-year={year}
              data-month={month}
              data-count={count}
            >
            </button>
          {/each}
        </div>
      </div>
    {/each}
  </div>

  <div class="current-indicator" style="top: 0; opacity: 0;"></div>
</div>

{#if tooltipData}
  <div
    class="tooltip-portal"
    style="top: {tooltipData.top}px; left: {tooltipData.left}px;"
  >
    {tooltipData.text}
  </div>
{/if}

<style lang="scss">
  .month-navigator {
    width: 48px;
    position: sticky;
    display: flex;
    flex-direction: column;
    background: var(--color-background-secondary);
    border-radius: var(--radius-lg);
    overflow: visible; /* Allow tooltips to overflow */
  }

  .navigator-content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: visible; /* Allow tooltips to overflow horizontally */
    max-height: calc(100vh - 200px);
    padding: var(--spacing-lg) 0;
  }

  .year-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .year-label {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-tertiary);
    margin-bottom: 12px;
    user-select: none;
  }

  .dots-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-sm);
    position: relative;
    overflow: visible; /* Allow tooltips to overflow */
  }

  .month-dot {
    position: relative;
    width: 6px;
    height: 6px;
    border-radius: var(--radius-full);
    background: rgba(0, 0, 0, 0.2);
    border: none;
    cursor: pointer;
    padding: 0;
    transition: all var(--transition-base);

    &.hovered {
      background: rgba(0, 0, 0, 0.4);
      transform: scale(1.3);
    }

    &.current {
      background: var(--color-primary-bright);
      width: 8px;
      height: var(--progress-bar-height);
      box-shadow: var(--shadow-glow-xs);
    }
  }

  .tooltip-portal {
    position: fixed;
    transform: translate(-100%, -50%);
    background: var(--color-text-primary);
    color: var(--color-background);
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    pointer-events: none;
    z-index: 10000;
    box-shadow: var(--shadow-lg);

    &::after {
      content: '';
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 5px solid transparent;
      border-left-color: var(--color-text-primary);
    }
  }

  .current-indicator {
    position: absolute;
    left: -4px;
    width: 3px;
    height: 20px;
    background: var(--color-primary-bright);
    border-radius: 0 2px 2px 0;
    transition: all var(--transition-slow) ease;
    pointer-events: none;
  }

  /* Custom scrollbar */
  .navigator-content::-webkit-scrollbar {
    width: var(--spacing-xs);
  }

  .navigator-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .navigator-content::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: var(--radius-sm);

    &:hover {
      background: rgba(0, 0, 0, 0.25);
    }
  }
</style>
